import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { customElement, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_TARGET_ITEM } from '../../../../../../../../webcomponents.constants';
import {
  getTargetContextFormOptions,
  getTargetProcessTypeFormOptionsForDropdown,
} from '../../../../../utils/uiSettings/applicationControl/application-control.values';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { ApplicationControlTargetProcessType } from '@nirvana/shared/dtos/types/policies/common';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { FormValidator } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { GzEnumFilterWebComponent } from '../../../../../../../../ui/gzEnumFilter';
import { isContextAvailable } from './grid-config';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { descriptionValidators, matchValidatorsByType } from '../utils/validators';
import { toggleDisabled } from '../../../../../../../../utils/htmlElement.utils';

@customElement(WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_TARGET_ITEM)
export class GzApplicationControlTargetItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  @query('#targetItemType')
  targetItemType: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#targetItemMatch')
  targetItemMatch: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#targetItemDescription')
  targetItemDescription: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#targetItemContext')
  targetItemContext: GzFormFieldWebComponent<GzEnumFilterWebComponent>;

  renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field">
        <gz-form-field id="targetItemType">
          <gz-dropdown
            @selectionChanged="${this.handleTypeFieldChange}"
            gz-form-input
            label="${translateFn('Authorization method')}"
            options="${JSON.stringify(getTargetProcessTypeFormOptionsForDropdown())}"
          ></gz-dropdown>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field input--w25">
        <gz-form-field id="targetItemMatch">
          <gz-input
            gz-form-input
            label="${translateFn('Authorization identifier')}"
            placeholder="${translateFn('Enter an authorization identifier')}"
          ></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field input--w25">
        <gz-form-field id="targetItemDescription">
          <gz-input
            gz-form-input
            label="${translateFn('Description')}"
            placeholder="${translateFn('Enter a description')}"
          ></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="targetItemContext">
          <gz-enum-filter
            gz-form-input
            label="${translateFn('Source')}"
            placeholder="${translateFn('Select source')}"
            confirmActionButtonString="${translateFn('Apply')}"
            filterOptions="${JSON.stringify(getTargetContextFormOptions())}"
          ></gz-enum-filter>
        </gz-form-field>
      </div>
    `;
  }

  focusFirstInput(): void {
    this.targetItemType.Input.updateDropdownState(true);
  }

  private handleTypeFieldChange(event: CustomEvent) {
    let matchPlaceholder: string;
    const contextValidators: FormValidator[] = [];

    switch (event.detail.value) {
      case ApplicationControlTargetProcessType.PATH:
        matchPlaceholder = translateFn('Enter a file path or pattern');
        contextValidators.push(RequiredValidator(translateFn('Required field')));
        break;
      case ApplicationControlTargetProcessType.HASH:
        matchPlaceholder = translateFn('Enter a hash value');
        break;
      case ApplicationControlTargetProcessType.CERTIFICATE:
        matchPlaceholder = translateFn('Enter a certificate thumbprint');
        break;
      default:
        matchPlaceholder = '';
    }

    const isAvailable = isContextAvailable(event.detail.value);
    toggleDisabled(this.targetItemContext, !isAvailable);
    if (!isAvailable) {
      this.targetItemContext.value = [];
    }

    this.targetItemMatch.Input.placeholder = matchPlaceholder;
    this.targetItemMatch.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => matchValidatorsByType(event.detail.value, value)),
    ];
    this.targetItemContext.Validators = contextValidators;
  }

  createFormGroup(): void {
    this.targetItemType.Validators = [RequiredValidator(translateFn('Required field'))];
    this.targetItemMatch.Validators = [RequiredValidator(translateFn('Required field'))];
    this.targetItemDescription.Validators = descriptionValidators();
    this.targetItemContext.Validators = [RequiredValidator(translateFn('Required field'))];

    this.formGroup = new FormGroup({
      type: this.targetItemType,
      match: this.targetItemMatch,
      description: this.targetItemDescription,
      context: this.targetItemContext,
    });
  }

  resetForm(): void {
    this.targetItemType.value = '';
    this.targetItemType.resetField();

    this.targetItemMatch.value = '';
    this.targetItemMatch.resetField();

    this.targetItemDescription.value = '';
    this.targetItemDescription.resetField();

    this.targetItemContext.value = [];
    this.targetItemContext.resetField();
  }

  reset() {
    this.resetForm();
  }

  resetOnAdd() {
    this.resetForm();
  }
}
